<template>
  <v-data-table
    v-model:expanded="expanded"
    :headers="headers"
    :items="items"
    :items-per-page="modelValue.itemsPerPage"
    item-value="number"
    :mobile-breakpoint="0"
    :sort-by="sortByForTable"
    :page="modelValue.page"
    :loading="filterLoading"
    class="dashboard-table"
    :hide-default-header="$vuetify.display.mdAndDown"
    @update:page="updateModel({ page: $event })"
    @update:sort-by="updateSortBy"
    @update:items-per-page="updateModel({ itemsPerPage: $event })"
  >
    <template #item="{ item, internalItem, isExpanded, toggleExpand, index }">
      <OpenBalancesMobileCard
        v-if="$vuetify.display.mdAndDown"
        :item="item"
        :active-account-id="activeAccountId"
        :class="index === 0 ? 'mt-0' : 'mt-2'"
      />
      <tr
        v-else
        :class="{ expanded: isExpanded(internalItem) }"
        class="sc-broker-ranking-broker sc-open-balances-row"
        @click="toggleExpand(internalItem)"
      >
        <td class="sc-data-list-select" style="width: 80px">
          <!-- Vuetify 3 renamed the list's ground to `bg-color`; as `color` it
               painted the default white surface, which showed as a white block
               inside an expanded row. -->
          <v-list bg-color="transparent">
            <v-list-item class="ml-0 pl-0 pr-0">
              <CommonChip class="sc-nbr">{{ item.number }}</CommonChip>
            </v-list-item>
          </v-list>
        </td>
        <td class="sc-data-list-subject">
          <router-link
            class="link-to-project"
            style="display: inline;"
            :to="getAccountRoute({ name: 'deal', params: { id: item._key }})"
          >
            {{ item.name }}
          </router-link>
          <br>
          <v-chip
            v-if="item.status === 'pending'"
            size="small"
            class="bg-info overline mt-2 text-white"
            :to="getAccountRoute({ name: 'deal', params: { id: item._key }})"
          >
            Pending invoice approval
          </v-chip>
        </td>
        <td class="sc-data-list">
          {{ $filters.formatDate(item.transactionDate) }}
        </td>
        <td class="sc-data-list">
          {{ $filters.formatCurrency(item.expected) }}
        </td>
        <td class="sc-data-list">
          {{ $filters.formatCurrency(item.payments) }}
        </td>
        <td class="sc-data-list">
          {{ $filters.formatCurrency(item.remaining) }}
        </td>
        <td class="sc-data-list">
          {{ $filters.formatCurrency(item.inHouseExpected) }}
        </td>
        <td class="sc-data-list">
          {{ $filters.formatCurrency(item.brokerGross) }}
        </td>
        <td class="sc-data-list">
          {{ $filters.formatCurrency(item.brokerGrossRemaining) }}
        </td>
        <td style="width: 24px">
          <v-btn
            :loading="exportLoadingKey === item._key"
            :disabled="exportLoadingKey && exportLoadingKey !== item._key"
            icon
            size="small"
            data-track="report.open-balances.export"
            @click.stop="$emit('click:export-single', item._key)"
          >
            <v-icon color="primary">mdi-file-export</v-icon>
          </v-btn>
        </td>
      </tr>
    </template>
    <template #expanded-row="{ columns, item }">
      <tr class="broker-ranking-detail">
        <td :colspan="columns.length">
          <v-card variant="outlined" class="ma-2">
            <OpenBalancesInvoiceTable :items-entry="item.invoices" />
          </v-card>
        </td>
      </tr>
    </template>
    <template #body.append>
      <tr v-if="!$vuetify.display.mdAndDown" class="bg-sc-surface-header tfoot">
        <td><v-chip>{{ totals.count }}</v-chip></td>
        <td />
        <td />
        <td>{{ $filters.formatCurrency(totals.expected) }} </td>
        <td>{{ $filters.formatCurrency(totals.paid) }}</td>
        <td>{{ $filters.formatCurrency(totals.remaining) }}</td>
        <td>{{ $filters.formatCurrency(totals.inHouseExpected) }} </td>
        <td>{{ $filters.formatCurrency(totals.brokerGross) }}</td>
        <td>{{ $filters.formatCurrency(totals.brokerGrossRemaining) }}</td>
        <td>
          <v-menu location="bottom end" style="width: 300px;">
            <template #activator="{ props }">
              <v-btn v-bind="props" icon>
                <v-icon color="primary">mdi-file-export</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item :loading="isExportAllLoading" data-track="report.open-balances-summary.export" @click="$emit('click:export-all')">
                <div>
                  Open Balances
                </div>
              </v-list-item>
              <v-list-item :loading="isExportAllLoading" data-track="report.open-balances-detailed.export" @click="$emit('click:export-all-detailed')">
                <div class="no-wrap">
                  Detailed Open Balances
                </div>
              </v-list-item>
            </v-list>
          </v-menu>
        </td>
      </tr>
    </template>
  </v-data-table>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { capitalize } from 'lodash-es';

import { IHeader, ITableOptions } from '@/bundles/BaseTable/interfaces';
import { AccountLayoutMixin } from '@/bundles/App/mixins/accountMixin';
import { IOpenBalanceListItem } from '@/bundles/Deal/types';
import { Nullable } from '@/utils/types';
import i18n from '@/bundles/Localization';
import { LocalizationEnum } from '@/bundles/Localization/enum/LocalizationEnum';

import CommonChip from '@/bundles/Common/components/chips/CommonChip.vue';
import OpenBalancesMobileCard from '@/bundles/Dashboard/components/OpenBalancesMobileCard.vue';
import OpenBalancesInvoiceTable from '@/components/reports/OpenBalancesInvoiceTable.vue';

interface IBrokerProductionTableData {
  headers: IHeader[];
  expanded: any[];
}

interface IBrokerProductionTableFooterTotals {
  count: number;
  expected: number;
  inHouseExpected: number;
  paid: number;
  brokerGross: number;
  remaining: number;
  brokerGrossRemaining: number;
}

// Vuetify 3 reads `title` / `key`; it falls back to `value` for the key but has
// no fallback for `text`, so v2-shaped headers render as blank column labels.
const headers = [
  { title: '#', key: '#', sortable: false },
  { title: 'Name', key: 'name', sortable: true },
  { title: 'Transaction Date', key: 'transactionDate', sortable: true },
  { title: 'Total Commission', key: 'expected', sortable: true },
  { title: 'Total Paid', key: 'payments', sortable: true },
  { title: 'Total Outstanding', key: 'remaining', sortable: true },
  { title: 'Total In-House Commission', key: 'inHouseExpected', sortable: true },
  {
    title: `${capitalize(i18n.tc(LocalizationEnum.broker))} Gross`,
    key: 'brokerGross',
    sortable: true,
  },
  {
    title: `${capitalize(i18n.tc(LocalizationEnum.broker))} Gross Outstanding`,
    key: 'brokerGrossRemaining',
    sortable: true,
  },
  { title: 'Export', key: 'export', sortable: false },
];

export default defineComponent({
  name: 'OpenBalancesTable',

  components: {
    OpenBalancesMobileCard,
    CommonChip,
    OpenBalancesInvoiceTable
  },
  mixins: [AccountLayoutMixin],

  props: {
    modelValue: {
      type: Object as PropType<ITableOptions>,
      required: true
    },
    items: {
      type: Array as PropType<IOpenBalanceListItem[]>,
      default: () => []
    },
    filterLoading: Boolean,
    isExportAllLoading: Boolean,
    exportLoadingKey: {
      type: String as PropType<Nullable<string>>,
      default: null
    }
  },

  emits: ['click:export-all', 'click:export-all-detailed', 'click:export-single', 'update:modelValue'],

  data: (): IBrokerProductionTableData => ({
    headers,
    expanded: [],
  }),

  computed: {
    // Vuetify 3's data table takes `sort-by` as [{ key, order }] and emits the
    // same shape; v2 used parallel sortBy / sortDesc arrays, which is still what
    // the table options model carries.
    sortByForTable (): { key: string; order: 'asc' | 'desc' }[] {
      const sortBy = this.modelValue.sortBy || [];
      const sortDesc = this.modelValue.sortDesc || [];

      return sortBy.map((key: string, index: number) => ({
        key,
        order: sortDesc[index] ? 'desc' : 'asc',
      }));
    },
    totals (): IBrokerProductionTableFooterTotals {
      const returnTotals = {
        count: this.items.length,
        expected: 0,
        inHouseExpected: 0,
        paid: 0,
        brokerGross: 0,
        remaining: 0,
        brokerGrossRemaining: 0
      }
      for (const item of this.items) {
        returnTotals.expected += item.expected;
        returnTotals.inHouseExpected += item.inHouseExpected;
        returnTotals.paid += item.payments;
        returnTotals.brokerGross += item.brokerGross;
        returnTotals.remaining += item.remaining;
        returnTotals.brokerGrossRemaining += item.brokerGrossRemaining;
      }
      return returnTotals
    }
  },

  methods: {
    updateModel (model: Partial<ITableOptions>) {
      this.$emit('update:modelValue', { ...this.modelValue, ...model });
    },
    updateSortBy (sortBy: { key: string; order: 'asc' | 'desc' }[]) {
      this.updateModel({
        sortBy: sortBy.map((item) => item.key),
        sortDesc: sortBy.map((item) => item.order === 'desc'),
      });
    },
  }
});
</script>
